import { Component, Input, forwardRef, EventEmitter, Output, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

import { BehaviorSubject, forkJoin, Subscription, Observable, last, catchError, of } from 'rxjs';

import { ReportSeriesModel } from '../../models/report-series.model';
import { AbstractFormFieldComponent } from '../../../../Common/PageBuilder/components/FormField/abstract-form-field.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ExternalDataSourcePickerFieldModel } from './external-data-source-picker-field.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { DataConnectorTypesEnum } from '../../../../api/externalData/enums/data-connector-types.enum';
import { DataConnectorModel } from '../../../../Core/Users/models/data-connector.model';
import { GoogleAnalyticsAccountModel } from '../../../../Core/Users/models/google-analytics-account.model';
import { GoogleAnalyticsReportRequestTypeEnum } from '../../../../api/externalData/enums/google-analytics-report-request-type.enum';
import { GoogleAnalyticsEntityApiModel } from '../../../../api/externalData/models/google-analytics-entity-api.model';
import { SeriesTypeEnum } from '../../enums/series-type.enum';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { DataConnectorsApi } from '../../../../api/externalData/data-connectors.api';
import { GoogleAnalyticsEntityModel } from '../../../../api/externalData/models/google-analytics-entity.model';
import { dateQueryInput24HoursOption } from '../../../../Common/Forms/models/date-query-input-options.model';
import { GoogleAnalyticsReportTypesEnum } from '../../../../api/externalData/enums/google-analytics-report-types.enum';
import { ChartDisplayElementModel } from '../../../../Common/Reports/models/chart-display-element.model';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';

@Component({
    selector: 'agpr-external-data-source-picker-field',
    templateUrl: './external-data-source-picker-field.component.html',
    styleUrls: ['./external-data-source-picker-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => ExternalDataSourcePickerFieldComponent)
        }
    ],
    standalone: false
})
export class ExternalDataSourcePickerFieldComponent extends AbstractFormFieldComponent implements OnInit, OnChanges, OnDestroy {
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly googleAnalyticsTypes = DataConnectorTypesEnum;

    useGoogleAnalyticsReferralSource: boolean;

    dataConnectors: DataConnectorModel[] = [];
    externalDataSourceOptions: DropdownOptionModel<DataConnectorModel>[] = [];
    selectedExternalDataSource: DropdownOptionModel<DataConnectorModel>;

    googleAnalyticsAccountOptions$: BehaviorSubject<GoogleAnalyticsAccountModel[]> = new BehaviorSubject([]);
    selectedGoogleAnalyticsAccount: GoogleAnalyticsAccountModel;

    allPropertiesMap: { [key: number]: GoogleAnalyticsEntityModel[] } = {};
    propertyOptions$: BehaviorSubject<GoogleAnalyticsEntityModel[]> = new BehaviorSubject([]);
    selectedProperty: GoogleAnalyticsEntityModel;

    viewOptions$: BehaviorSubject<GoogleAnalyticsEntityApiModel[]> = new BehaviorSubject([]);
    selectedView: GoogleAnalyticsEntityApiModel;

    @Input() declare field: ExternalDataSourcePickerFieldModel;
    @Input() declare value: ReportSeriesModel[];
    @Output() changes: EventEmitter<ReportSeriesModel[]> = new EventEmitter<ReportSeriesModel[]>();

    series: ReportSeriesModel;

    subscriptions: Subscription[] = [];

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private dataConnectorsApi: DataConnectorsApi,
        private dataConnectionService: DataConnectionService
    ) {
        super();
    }

    ngOnInit(): void {
        if (!this.series) {
            if (this.value && this.value.length > 0) {
                this.value = this.value.slice();
                this.series = this.value[0];
            } else {
                this.series = new ReportSeriesModel(null, this.block.type.key as ReportBlockTypeEnum, SeriesTypeEnum.googleAnalytics);
                const mentionSeries = (this.block.displayElement as ChartDisplayElementModel).series[0];
                if (mentionSeries && !mentionSeries.externalData) {
                    // the first series is mention series, use its date range
                    this.series.dateRange = mentionSeries.dateRange;
                } else {
                    this.series.dateRange = new ReportDateRangeModel().setFromDateQuery(dateQueryInput24HoursOption.value.query);
                }
            }
        }

        this.subscriptions.push(
            this.dataConnectionService.googleAnalyticsDataConnectors$.subscribe(dataConnectors => {
                if (!dataConnectors) {
                    return;
                }

                this.dataConnectors = dataConnectors;

                this.externalDataSourceOptions = dataConnectors.map(
                    dataConnector => {
                        return new DropdownOptionModel(
                            dataConnector.name + (dataConnector.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4 ? ' (GA4)' : ' (GA3)'),
                            dataConnector
                        );
                    }
                );

                this.allPropertiesMap = {};
                const orderedAccounts = [];
                const requests: Observable<GoogleAnalyticsEntityApiModel[]>[] = [];
                dataConnectors.forEach(connector => {
                    connector.googleAnalyticsAccounts?.forEach(account => {
                        this.allPropertiesMap[account.id] = [] as GoogleAnalyticsEntityModel[];

                        orderedAccounts.push({ account: account, type: connector.type });
                        requests.push(
                            this.dataConnectorsApi.getGoogleAnalyticsProperties(connector.id, account.id)
                                .pipe(
                                    catchError(error => {
                                        return of([]);
                                    }),
                                    last()
                                )
                        );
                    });
                });

                forkJoin(requests).subscribe(results => {
                    results.forEach((properties, i) => {
                        this.allPropertiesMap[orderedAccounts[i].account.id].push(...(<any[]>properties).map(
                            property => {
                                const prop = new GoogleAnalyticsEntityModel(property);
                                prop.dataConnectorType = orderedAccounts[i].type;
                                return prop;
                            }
                        ));
                    });

                    this.populateFieldsFromValue();

                    // if we're here, and there isn't a selected source, select the first available (if present)
                    if (!this.selectedExternalDataSource && this.externalDataSourceOptions[0]) {
                        this.selectedExternalDataSource = this.externalDataSourceOptions[0];
                        this.handleExternalDataSourceSelection();
                    }
                });
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            if (this.series && this.value.length) {
                this.series.dateRange = this.value[0].dateRange;
            }
        }
    }

    populateFieldsFromValue(): void {
        this.selectedExternalDataSource = this.externalDataSourceOptions.find(option => {
            return option.value.id === this.series?.externalData?.dataConnectorId;
        });

        if (this.selectedExternalDataSource?.value) {
            this.googleAnalyticsAccountOptions$.next(this.selectedExternalDataSource.value.googleAnalyticsAccounts);
        }

        this.selectedGoogleAnalyticsAccount = this.googleAnalyticsAccountOptions$.getValue().find(account => {
            return account.id === this.series?.externalData.googleAnalyticsAccountId;
        });

        if (this.selectedGoogleAnalyticsAccount) {
            this.propertyOptions$.next(
                this.allPropertiesMap[this.selectedGoogleAnalyticsAccount.id]
                    .filter(property => property.dataConnectorType === this.selectedExternalDataSource.value.type)
            );
        }

        this.selectedProperty = this.propertyOptions$.getValue().find(property => {
            return property.id === this.series?.externalData.googleAnalyticsPropertyId;
        });
        if (this.selectedProperty) {
            this.handlePropertyChange();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    emitChanges(): void {
        this.value = [ this.series ];
        this.changes.emit(this.value);
    }

    handleExternalDataSourceSelection(event?: any): void {
        this.selectedGoogleAnalyticsAccount = null;
        this.selectedProperty = null;
        this.selectedView = null;

        if (this.selectedExternalDataSource?.value) {
            this.googleAnalyticsAccountOptions$.next(this.selectedExternalDataSource.value.googleAnalyticsAccounts);
        }

        this.handleViewChange();
    }

    handleGoogleAnalyticsAccountChange(event?: any): void {
        this.selectedProperty = null;
        this.selectedView = null;

        if (this.selectedGoogleAnalyticsAccount) {
            this.propertyOptions$.next(this.allPropertiesMap[this.selectedGoogleAnalyticsAccount.id]
                .filter(property => property.dataConnectorType === this.selectedExternalDataSource.value.type));
        }

        this.handleViewChange();
    }

    handlePropertyChange(event?: any): void {
        if (this.series.externalData) {
            this.series.externalData.googleAnalyticsPropertyId = this.selectedProperty.id;
            this.emitChanges();
        }

        if (this.selectedExternalDataSource.value.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS) {
            this.dataConnectorsApi
                .getGoogleAnalyticsProfiles(this.selectedExternalDataSource.value.id, this.selectedGoogleAnalyticsAccount.id, this.selectedProperty.id)
                .subscribe(res => {
                    const views: GoogleAnalyticsEntityApiModel[] = [];
                    if (res) {
                        res.forEach(item => views.push({
                            id: item.id,
                            name: item.name
                        } as GoogleAnalyticsEntityApiModel));
                    }

                    this.viewOptions$.next(views);

                    // prepopulate the view if it has already been set
                    if (this.value[0]?.externalData) {
                        this.selectedView = views.find(view => view.id === this.value[0].externalData.externalDataId);
                    }

                    // prepopulate the first dropdown item if no initial value is found
                    if (!this.selectedView && views.length > 0) {
                        this.selectedView = views[0];
                    }

                    if (event) {
                        this.handleViewChange(true);
                    }
                });
        } else {
            this.series.externalData = {
                dataConnectorId: this.selectedExternalDataSource?.value?.id,
                externalDataId: this.selectedProperty.id,
                externalDataType: DataConnectorTypesEnum.GOOGLE_ANALYTICS_4,
                googleAnalyticsAccountId: this.selectedGoogleAnalyticsAccount?.id,
                googleAnalyticsPropertyId: this.selectedProperty?.id,
                timeZone: this.selectedProperty?.timeZone
            };
            this.series.chartType = this.block.type.key as ReportBlockTypeEnum;
            this.series.type = SeriesTypeEnum.googleAnalytics;
            this.series.label = this.selectedExternalDataSource?.label;
            if (event) {
                if (this.series.chartType === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND) {
                    (this.block.displayElement as ChartDisplayElementModel).referralTypes = [
                        GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS,
                        GoogleAnalyticsReportTypesEnum.SESSIONS,
                        GoogleAnalyticsReportTypesEnum.USERS
                    ];
                } else {
                    (this.block.displayElement as ChartDisplayElementModel).referralTypes = [
                        GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS,
                        GoogleAnalyticsReportTypesEnum.WEB_REFERRALS,
                        GoogleAnalyticsReportTypesEnum.SOCIAL_REFERRALS,
                        GoogleAnalyticsReportTypesEnum.EMAIL_REFERRALS
                    ];
                }
            }

            this.emitChanges();
        }
    }

    handleViewChange(event?: any): void {
        this.series.externalData = {
            dataConnectorId: this.selectedExternalDataSource?.value?.id,
            externalDataId: this.selectedView?.id,
            externalDataType: this.selectedExternalDataSource?.value.type || DataConnectorTypesEnum.GOOGLE_ANALYTICS,
            googleAnalyticsReportType: GoogleAnalyticsReportRequestTypeEnum.REFERRAL_TREND,
            googleAnalyticsAccountId: this.selectedGoogleAnalyticsAccount?.id,
            googleAnalyticsPropertyId: this.selectedProperty?.id
        };
        this.series.chartType = this.block.type.key as ReportBlockTypeEnum;
        this.series.type = SeriesTypeEnum.googleAnalytics;
        this.series.label = this.selectedExternalDataSource?.label;

        // needs to check event to differentiate whether the option is set at initialization or by user action
        if (event) {
            if (this.selectedExternalDataSource.value.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS) {
                (this.block.displayElement as ChartDisplayElementModel).referralTypes = [
                    GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS,
                    GoogleAnalyticsReportTypesEnum.SESSIONS,
                    GoogleAnalyticsReportTypesEnum.USERS
                ];
            } else {
                (this.block.displayElement as ChartDisplayElementModel).referralTypes = [
                    GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS,
                    GoogleAnalyticsReportTypesEnum.SESSIONS,
                    GoogleAnalyticsReportTypesEnum.USERS
                ];
            }
        }

        this.emitChanges();
    }
}
